/**
 * Hook for managing the Ask Hydrologiq chatbot state
 */

import { useState, useCallback } from "react"
import { v4 as uuidv4 } from "uuid"
import { useMutation } from "@apollo/client"
import {
  SendChatMessageDocument,
  type SendChatMessageMutation,
  type SendChatMessageMutationVariables,
} from "@hydrologiq/gql-experience/graphql"
import { ChatMessage, ChatState, StructuredAIResponse } from "./types"

export interface UseChatbotReturn {
  messages: ChatMessage[]
  isLoading: boolean
  isOpen: boolean
  error: string | null
  sendMessage: (content: string) => Promise<void>
  openChat: () => void
  closeChat: () => void
  toggleChat: () => void
  clearMessages: () => void
}

export const useChatbot = (): UseChatbotReturn => {
  const [state, setState] = useState<ChatState>({
    messages: [],
    isLoading: false,
    isOpen: false,
    error: null,
  })

  const [sendChatMessageMutation] = useMutation<
    SendChatMessageMutation,
    SendChatMessageMutationVariables
  >(SendChatMessageDocument)

  const openChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }))
  }, [])

  const closeChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }))
  }, [])

  const toggleChat = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen }))
  }, [])

  const clearMessages = useCallback(() => {
    setState((prev) => ({ ...prev, messages: [], error: null }))
  }, [])

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return

    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: "user",
      content: content.trim(),
      timestamp: new Date(),
    }

    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      error: null,
    }))

    try {
      const { data } = await sendChatMessageMutation({
        variables: {
          message: content.trim(),
        },
      })

      if (!data?.sendChatMessage?.message) {
        throw new Error("No response from AI")
      }

      const structured = normalizeStructuredResponse(data.sendChatMessage)

      const assistantMessage: ChatMessage = {
        id: uuidv4(),
        role: "assistant",
        content: structured.message,
        structured,
        timestamp: new Date(),
      }

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
        isLoading: false,
      }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error"
      
      const errorAssistantMessage: ChatMessage = {
        id: uuidv4(),
        role: "assistant",
        content: "Sorry, I encountered an error. Please try again.",
        timestamp: new Date(),
      }

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, errorAssistantMessage],
        isLoading: false,
        error: errorMessage,
      }))
    }
  }, [sendChatMessageMutation])

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    isOpen: state.isOpen,
    error: state.error,
    sendMessage,
    openChat,
    closeChat,
    toggleChat,
    clearMessages,
  }
}


const normalizeStructuredResponse = (
  response: SendChatMessageMutation["sendChatMessage"]
): StructuredAIResponse => {
  const message = response?.message?.trim() || "I couldn't generate a response."
  const entities =
    response?.entities
      ?.filter(Boolean)
      .map((entity) => ({
        type: entity?.type || "entity",
        id: entity?.id || undefined,
        name: entity?.name || undefined,
      })) || []

  const highlights =
    response?.highlights
      ?.filter(Boolean)
      .map((highlight) => ({
        label: highlight?.label || "Highlight",
        value: highlight?.value || "",
        entityId: highlight?.entityId || undefined,
      })) || []

  const metadata = response?.metadata || {}

  return {
    message,
    entities,
    highlights,
    metadata: {
      generatedAt: metadata.generatedAt || new Date().toISOString(),
      truncated: Boolean(metadata.truncated),
      note: metadata.note || undefined,
    },
  }
}


